import React, { useEffect, useLayoutEffect, useRef } from 'react'
import Scene from './Scene'
import { stepPhase, useSky, onSunStep } from '../../hooks/useSky'

// useLayoutEffect warns during server rendering; the effect only matters in
// the browser, so the server gets the harmless useEffect.
const useBrowserLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Hover: the halo comes up quickly, then breathes for as long as the pointer stays.
const BLOOM_IN = [
  { opacity: 0, transform: 'scale(0.92)' },
  { opacity: 0.9, transform: 'scale(1.12)' },
]
const BREATHE = [
  { opacity: 0.9, transform: 'scale(1.12)', offset: 0 },
  { opacity: 0.5, transform: 'scale(1.3)', offset: 0.5 },
  { opacity: 0.9, transform: 'scale(1.12)', offset: 1 },
]

// Click: squash, stretch, then wobble down to rest.
const JIGGLE = [
  { transform: 'scale(1, 1) rotate(0deg)', offset: 0 },
  { transform: 'scale(1.09, 0.93) rotate(-3deg)', offset: 0.18 },
  { transform: 'scale(0.95, 1.06) rotate(2.4deg)', offset: 0.38 },
  { transform: 'scale(1.04, 0.97) rotate(-1.4deg)', offset: 0.58 },
  { transform: 'scale(0.985, 1.02) rotate(0.7deg)', offset: 0.78 },
  { transform: 'scale(1, 1) rotate(0deg)', offset: 1 },
]

const SkyFrame = ({ variant }) => {
  const sky = useSky()
  const landscape = (sky && sky.scene) || 'bay'
  const frameRef = useRef(null)
  const sunRef = useRef(null)
  const hitRef = useRef(null)
  const glowRef = useRef(null)
  const lastVariant = useRef(variant)
  const lastRect = useRef(null)

  // Keep the frame's last laid-out geometry, so a variant change knows where
  // it is morphing from. ResizeObserver reports after layout, so at the
  // moment the variant flips this still holds the previous state.
  useEffect(() => {
    const el = frameRef.current
    const svg = el && el.querySelector('.sky-scene')
    if (!el || !svg || typeof ResizeObserver === 'undefined') return undefined
    const measure = () => {
      const b = el.getBoundingClientRect()
      const r = svg.getBoundingClientRect()
      lastRect.current = { h: b.height, top: r.top - b.top, w: r.width }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // When the variant changes, morph from the previous geometry to the new
  // one. The frame's height and the scene's offset and width are animated
  // with pixel keyframes through the Web Animations API rather than CSS
  // transitions: the scene's --sun-range must ease at the same time, and
  // WebKit drops every other animated value on an element while one of its
  // custom properties is animating, so a transition on the scene froze its
  // offset. The range transitions on the sun group instead (see _sky.scss),
  // and this animation only touches physical properties (WebKit also drops
  // logical names from keyframes). This layout effect runs after the new
  // variant is in the DOM and before paint, so measuring gives the target.
  useBrowserLayoutEffect(() => {
    const el = frameRef.current
    if (!el || lastVariant.current === variant) return undefined
    lastVariant.current = variant
    el.setAttribute('data-morphing', '')
    const t = setTimeout(() => el.removeAttribute('data-morphing'), 700)

    const from = lastRect.current
    const svg = el.querySelector('.sky-scene')
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (from && svg && el.animate && !reduce) {
      const b = el.getBoundingClientRect()
      const r = svg.getBoundingClientRect()
      const to = { h: b.height, top: r.top - b.top, w: r.width }
      const timing = { duration: 600, easing: 'cubic-bezier(0.2, 0.7, 0.25, 1)' }
      el.animate([{ height: `${from.h}px` }, { height: `${to.h}px` }], timing)
      svg.animate(
        [{ top: `${from.top}px`, width: `${from.w}px` }, { top: `${to.top}px`, width: `${to.w}px` }],
        timing
      )
    }
    return () => clearTimeout(t)
  }, [variant])

  // ?debug=sky reports what this browser computed for the sun (see
  // src/lib/skyDebug.js); the module is only fetched when asked for.
  useEffect(() => {
    if (!/[?&]debug=sky(?:&|$)/.test(window.location.search)) return undefined
    let stop = null
    let gone = false
    import('../../lib/skyDebug').then((m) => { if (gone) return; stop = m.startSkyDebug() })
    return () => { gone = true; if (stop) stop() }
  }, [])

  // Nothing needs to animate while the scene is scrolled out of view. The
  // frame is fixed now, so "out of view" means the dusk has covered it:
  // three quarters of a viewport down, the tint is near its full depth.
  useEffect(() => {
    const el = frameRef.current
    if (!el) return undefined
    let raf = 0
    const check = () => {
      raf = 0
      el.toggleAttribute('data-offscreen', window.scrollY > window.innerHeight * 0.75)
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check) }
    check()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf) }
  }, [])

  // Only show the sun's focus ring when someone is actually tabbing around.
  useEffect(() => {
    const root = document.documentElement
    const onKey = (e) => { if (e.key === 'Tab') root.dataset.kbd = '1' }
    const onPointer = () => { delete root.dataset.kbd }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('mousedown', onPointer, true)
    window.addEventListener('touchstart', onPointer, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('mousedown', onPointer, true)
      window.removeEventListener('touchstart', onPointer, true)
    }
  }, [])

  const play = (el, keyframes, options) => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!el || !el.animate || reduce) return
    el.getAnimations().forEach((a) => a.cancel())
    el.animate(keyframes, options)
  }

  // Whatever steps the hour, the sun wobbles.
  useEffect(() => onSunStep(() => {
    play(sunRef.current, JIGGLE, { duration: 1200, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' })
  }), [])

  const activate = (e) => {
    stepPhase('sun')
    // A pointer click leaves focus behind; a keyboard activation (detail 0) keeps it.
    if (e.detail > 0 && hitRef.current) hitRef.current.blur()
  }

  // The content reads over the scene, so a line of text can sit on the sun.
  // A click on such text (not on a link or control, and not the end of a
  // drag that selected something) is passed to the sun.
  useEffect(() => {
    let down = null
    const onDown = (e) => { down = { x: e.clientX, y: e.clientY } }
    const onClick = (e) => {
      const hit = hitRef.current
      if (!hit || e.target === hit || !down) return
      const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y) > 6
      const interactive = e.target.closest && e.target.closest('a, button, input, textarea, select, label, [role="button"]')
      const selecting = window.getSelection && !window.getSelection().isCollapsed
      if (moved || interactive || selecting) return
      if (document.elementsFromPoint(e.clientX, e.clientY).includes(hit)) activate({ detail: e.detail })
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('click', onClick)
    }
  }, [])

  // Hovering the sun makes its halo breathe for as long as the pointer stays,
  // so it reads as something to press; leaving fades it out from wherever it is.
  const hovering = useRef(false)
  const preview = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return
    const glow = glowRef.current
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!glow || !glow.animate || reduce) return
    hovering.current = true
    glow.getAnimations().forEach((a) => a.cancel())
    const rise = glow.animate(BLOOM_IN, { duration: 250, easing: 'ease-out', fill: 'forwards' })
    rise.onfinish = () => {
      if (!hovering.current) return
      rise.cancel()
      glow.animate(BREATHE, { duration: 2400, easing: 'ease-in-out', iterations: Infinity })
    }
  }
  const settle = () => {
    hovering.current = false
    const glow = glowRef.current
    if (!glow || !glow.animate) return
    const from = parseFloat(getComputedStyle(glow).opacity) || 0
    const scale = getComputedStyle(glow).transform
    glow.getAnimations().forEach((a) => a.cancel())
    if (from > 0) {
      glow.animate(
        [{ opacity: from, transform: scale }, { opacity: 0, transform: 'scale(1.4)' }],
        { duration: 900, easing: 'ease-out' }
      )
    }
  }

  return (
    <figure className="sky" data-variant={variant} ref={frameRef}>
      <Scene variant={variant} landscape={landscape} sunRef={sunRef} hitRef={hitRef} glowRef={glowRef} onSunActivate={activate} onSunHover={preview} onSunLeave={settle} />
    </figure>
  )
}

export default SkyFrame
